import type React from 'react';
import { PanelAction, ACTIONS } from '@wix/component-protocol/schema';
import { AppData } from '@wix/document-services-types';
import { Host, AuthenticationStrategy } from '@wix/sdk-types';
import {
  createEditorPlatformManifestServicesError,
  EditorPlatformManifestServicesErrorCode,
} from '../errors';

type IPanelConfig = NonNullable<PanelAction['inline']>['code'];
type IComponentPanelFactory = (props: {
  host: Host;
  auth: AuthenticationStrategy;
}) => React.ReactNode;

export class PanelLoader {
  #loadPanelCache = new Map<string, Promise<IComponentPanelFactory>>();

  load(panelAction: PanelAction, appData: AppData) {
    const panelConfig = this.#getPanelConfig(panelAction, appData);

    if (!panelConfig) {
      return null;
    }

    return this.#load(panelConfig);
  }

  #load(panelConfig: IPanelConfig) {
    const { bundleUrl } = panelConfig!;

    if (!bundleUrl) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.LoadComponentPanelError,
        'No bundleUrl provided for the panel',
      );
    }

    const cached = this.#loadPanelCache.get(bundleUrl);
    if (cached) {
      return cached;
    }

    const loadPanelPromise = this.#loadPanel(panelConfig);
    this.#loadPanelCache.set(bundleUrl, loadPanelPromise);

    loadPanelPromise.finally(() => {
      this.#loadPanelCache.delete(bundleUrl);
    });

    return loadPanelPromise;
  }

  async #loadPanel(panelConfig: IPanelConfig) {
    try {
      const { bundleUrl, cssUrl } = panelConfig!;

      if (cssUrl) {
        this.#loadCSS(cssUrl);
      }

      // Import the ESM module
      const module = await this.#importModule(bundleUrl!);

      const factory = module.default;

      if (!factory || typeof factory !== 'function') {
        throw createEditorPlatformManifestServicesError(
          EditorPlatformManifestServicesErrorCode.LoadComponentPanelError,
          'invalid loaded panel factory type',
        );
      }

      return factory;
    } catch (error: any) {
      console.error(error?.message);
      // propagate the error
      throw error;
    }
  }

  #loadCSS(cssUrl: string): void {
    if (document.querySelectorAll(`link[href="${cssUrl}"]`).length) {
      return;
    }

    const linkElement = document.createElement('link');
    linkElement.rel = 'stylesheet';
    linkElement.href = cssUrl;

    document.head.appendChild(linkElement);
  }

  async #importModule(bundleUrl: string) {
    try {
      return (await import(/* webpackIgnore: true */ bundleUrl)) as Promise<{
        default: IComponentPanelFactory;
      }>;
    } catch (error) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.LoadComponentPanelError,
        "failed to import panel's bundle",
      );
    }
  }

  #getPanelConfig(panel: PanelAction, appData: AppData): IPanelConfig | null {
    switch (panel.panelType) {
      case ACTIONS.PANEL_TYPE.panelId: {
        // TODO: check type
        const panelComponent = appData?.components?.find(
          (component) => component.componentId === panel.panelId,
        );

        if (
          // @ts-expect-error no panel related types in app data
          panelComponent?.data?.contentType === ACTIONS.PANEL_CONTENT_TYPE.code
        ) {
          // @ts-expect-error no panel related types in app data
          return panelComponent?.data?.code;
        }

        return null;
      }

      case ACTIONS.PANEL_TYPE.inline: {
        if (panel.inline?.contentType === ACTIONS.PANEL_CONTENT_TYPE.code) {
          return panel.inline?.code;
        }

        return null;
      }

      default: {
        return null;
      }
    }
  }
}
